import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Skeleton,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { sentryTrace } from 'utils/sentryTrace';
import { trackBeginBooking, trackBookClass, trackBookClassFailed, trackViewClass } from 'utils/tracking';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useFetchClass, useFetchClassWaitlistEntry, useListClientServices, useListVisits } from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import Auth from 'screens/Account/Auth';
import { ClassRoomLayout, ClassRoomLayoutLoadingPlaceholder } from 'screens/Class/components/ClassRoomLayout';
import { Services } from 'screens/Prices/components/Services';
import { ServicesContracts } from 'screens/Prices/components/ServicesContracts';
import { MindbodyClass, PickaspotReservation } from 'types/class';
import { ClientVisit } from 'types/client';
import { ApiClientException } from 'utils/apiClientException';
import { ApiClientExceptionErrorCode } from 'types/api';
import { capitalizeFirstLetter, classHasCapacity, isClassBookable } from 'utils/functions';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import { getBrandColor } from 'utils/brandColor';
import { filterAllowedServices } from 'utils/serviceRestrictions';

export interface ReservationFormValues {
  SpotNumber?: number;
  serviceId?: number;
  classId: string;
  Waitlist?: boolean | number;
  isCommunityClass?: boolean;
  isEnrollment?: boolean;
}

const buttonBorderRadius = `${mondayMindbody?.customization?.buttonBorderRadius}px`;

export function ClassScreen() {
  const queryClient = useQueryClient();

  const [selectedService, setSelectedService] = useState<number>();
  const [isLoadingServiceConfirmation, setIsLoadingServiceConfirmation] = useState(false);

  const [hasAlreadyUsedRoutineService, setHasAlreadyUsedRoutineService] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const isEnrollmentInURL = location.pathname.includes('enrollment');
  const isCourseFromState = (location.state as any)?.isCourse === true;
  const { isLoggedIn, user } = useUserContext();
  const toast = useToast();
  const formMethods = useForm<ReservationFormValues>({});
  const { formState, handleSubmit, resetField } = formMethods;

  const { refetch: refetchVisits } = useListVisits() || {};
  const { data: servicesData, refetch: refetchClientServices } = useListClientServices();
  const { data: visits } = useListVisits() || {};
  const {
    data: mindbodyClass,
    error,
    isLoading: classLoading,
    refetch: refetchClass,
  } = useFetchClass(id, isEnrollmentInURL);
  const { data: waitlistEntry, refetch: refetchWaitlist } = useFetchClassWaitlistEntry(id);

  // Tunnel de réservation : fiche affichée.
  useEffect(() => {
    if (mindbodyClass) trackViewClass(mindbodyClass);
  }, [mindbodyClass]);

  // Tarifs autorisés pour la séance (null : tous), contrôlés aussi côté serveur à la réservation.
  const allowedServiceNames = mindbodyClass?.AllowedServiceNames;
  const clientServices = servicesData ? filterAllowedServices(Object.values(servicesData), allowedServiceNames) : [];
  const noRoutineServices = clientServices.filter((service) => !service.Name.toLowerCase().includes('challenge'));

  // Détecter si la classe n'a pas de plan de salle
  const hasNoRoomLayout = !mindbodyClass?.PickASpot;

  const visitInformations: ClientVisit | undefined = useMemo(() => {
    if (!visits) return undefined;
    return visits.find((visit) => visit.ClassId === Number(id));
  }, [visits, id]);

  useEffect(() => {
    if (visitInformations && visitInformations.Service.Name.toLowerCase().includes('challenge')) {
      setHasAlreadyUsedRoutineService(true);
    }
  }, [visitInformations]);

  const selectedSpot = formMethods.watch('SpotNumber');
  const primaryColor = getBrandColor();
  const isCourse = mindbodyClass?.IsCourse || isCourseFromState;
  const sessionTypeNumDeducted = mindbodyClass?.ClassDescription?.SessionType?.NumDeducted ?? 1;
  // Certains events spéciaux (RIISE 5 ANS) regroupent tous les cours sous le même
  // Location alors que chaque SessionType garde son propre NumDeducted en base
  // Mindbody — seul le Reformer est correctement à 2, YOGA/PILATES restent à 1.
  // On force la valeur attendue par l'event quand le signal Location est présent.
  const isRiise5AnsEvent = /^\s*riise\s*5\s*ans\s*$/i.test(mindbodyClass?.Location?.Name ?? '');
  const creditsRequired = Math.max(1, sessionTypeNumDeducted, isRiise5AnsEvent ? 2 : 1);
  const isMultiCreditClass = creditsRequired > 1;
  const selectedServiceData = useMemo(
    () => clientServices.find((service) => Number(service.Id) === selectedService),
    [clientServices, selectedService],
  );
  const hasEnoughCreditsOnSelectedService =
    !selectedServiceData || (selectedServiceData.Remaining ?? 0) >= creditsRequired;

  useEffect(() => {
    if (mindbodyClass) {
      if (!isClassBookable(mindbodyClass) && !isEnrollmentInURL && !hasNoRoomLayout && !isCourse) {
        toast({
          title: t("Ce cours n'est pas réservable"),
          status: 'error',
        });
      }
    }
  }, [mindbodyClass, toast, t, isEnrollmentInURL, isCourse]);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  async function bookClass(body: Partial<ReservationFormValues> = {}) {
    Object.assign(body, { classId: id });
    body.isEnrollment = isEnrollmentInURL;

    const bookEndpoint = isCourse ? '/class/book-course' : '/class/book';
    const bookingTrack = { waitlist: !!body.Waitlist, serviceId: body.serviceId, course: !!isCourse };
    trackBeginBooking(mindbodyClass, bookingTrack);

    try {
      await HTTP.post<PickaspotReservation>(bookEndpoint, body).send();
      trackBookClass(mindbodyClass, bookingTrack);

      // Log successful booking to Sentry for traceability
      sentryTrace('Class booking successful', {
        level: 'info',
        tags: {
          category: 'booking',
          action: 'book_class',
          isWaitlist: body.Waitlist ? 'true' : 'false',
          isEnrollment: body.isEnrollment ? 'true' : 'false',
          isCourse: isCourse ? 'true' : 'false',
          isMultiCredit: isMultiCreditClass ? 'true' : 'false',
        },
        extra: {
          classId: id,
          serviceId: body.serviceId,
          spotNumber: body.SpotNumber,
          isCommunityClass: body.isCommunityClass,
          creditsRequired,
          locationId: mindbodyClass?.Location?.Id,
          locationName: mindbodyClass?.Location?.Name,
          user: `${user?.FirstName} ${user?.LastName}`,
          email: user?.Email,
          clientId: user?.Id,
          siteId: mondayMindbody?.siteId,
        },
      });

      resetField('SpotNumber');
      setShowSuccessMessage(true);
      await Promise.all([refetchVisits(), refetchClientServices(), refetchClass(), refetchWaitlist()]);
      if (
        body.serviceId === 49537 || // 113072
        body.serviceId === 51802 || // 113072
        body.serviceId === 21630 || // 113081
        body.serviceId === 12867 // 113077
      ) {
        setHasAlreadyUsedRoutineService(true);
      }
    } catch (error) {
      trackBookClassFailed(
        mindbodyClass,
        error instanceof ApiClientException ? error.ErrorCode : undefined,
        bookingTrack,
      );
      Sentry.withScope((scope) => {
        scope.setTag('where', 'ClassScreen:bookClass');
        scope.setTag('isMultiCredit', isMultiCreditClass ? 'true' : 'false');
        scope.setExtra('classId', id);
        scope.setExtra('creditsRequired', creditsRequired);
        scope.setExtra('locationId', mindbodyClass?.Location?.Id);
        scope.setExtra('locationName', mindbodyClass?.Location?.Name);
        scope.setExtra('body', body);
        // ErrorCode en tag : filtrable/groupable dans Sentry (ex. tous les
        // refus « Only new clients qualify » d'un coup).
        if (error instanceof ApiClientException) {
          scope.setTag('mb_error_code', String(error.ErrorCode));
          scope.setExtra('mb_message', error.Message);
        }
        Sentry.captureException(error);
      });

      if (error instanceof ApiClientException) {
        let title = error.getMessage();

        // Check if the error is related to full waitlist
        if (
          error.Message.includes('ClassSignUpsFull') ||
          error.Message.includes('Registration for the class is full')
        ) {
          title = t("La liste d'attente est complète");
        }

        if (error.ErrorCode === ApiClientExceptionErrorCode.InsufficientCredits) {
          title = error.Message;
        }

        // Erreurs « crédit non applicable » détectées par le serveur (framework Wix
        // UX) : le titre vient du backend avec la raison précise (pack pas encore
        // actif, pack expirant avant la séance, crédit refusé par Mindbody), la
        // description rassure et guide.
        const creditErrorDescriptions: Record<string, string> = {
          ServiceNotYetActive: t(
            'Votre place n’a pas été réservée et rien n’a été débité. Choisissez une séance après la date d’activation de votre pack, ou utilisez un autre crédit.'
          ),
          ServiceExpiresBeforeClass: t(
            'Votre place n’a pas été réservée et rien n’a été débité. Choisissez une séance avant l’expiration de votre pack, ou renouvelez-le depuis la page Tarifs.'
          ),
          ServiceNotApplicable: t(
            'Votre réservation a été annulée automatiquement et rien n’a été débité. Ce pack semble réservé à un autre studio ou ne pas s’appliquer à cette séance. Essayez un autre crédit, ou contactez-nous si vous pensez qu’il s’agit d’une erreur.'
          ),
        };
        const creditErrorDescription = creditErrorDescriptions[error.ErrorCode as string];

        const isPreciseCreditError =
          error.ErrorCode === ApiClientExceptionErrorCode.InsufficientCredits || Boolean(creditErrorDescription);

        const apiDescription =
          creditErrorDescription ??
          (error.ErrorCode === ApiClientExceptionErrorCode.InsufficientCredits
            ? undefined
            : resolveUserError('booking', error).description);

        toast({
          title,
          description: apiDescription,
          status: 'error',
          duration: isPreciseCreditError ? 10000 : undefined,
          isClosable: true,
        });

        refetchVisits();
        await Promise.all([refetchClass(), refetchWaitlist()]);
      } else {
        const { title, description } = resolveUserError('booking', error);
        toast({ title, description, status: 'error', isClosable: true });
      }
    }
  }

  // Run Club (ProgramId 46): achat Comp 0€ + booking atomique, sans modale de sélection
  const isParisSite = ['5736526', '5736555', '5736551'].includes(mondayMindbody?.siteId);
  const isRunClub = isParisSite && mindbodyClass?.ClassDescription?.Program?.Id === 46;

  const bookRunClub = async () => {
    setIsLoadingServiceConfirmation(true);
    trackBeginBooking(mindbodyClass, { runClub: true });
    try {
      await HTTP.post('/class/book-runclub', { classId: id }).send();
      trackBookClass(mindbodyClass, { runClub: true });

      sentryTrace('Run Club booking successful', {
        level: 'info',
        tags: { category: 'booking', action: 'book_runclub' },
        extra: {
          classId: id,
          user: `${user?.FirstName} ${user?.LastName}`,
          email: user?.Email,
        },
      });

      setShowSuccessMessage(true);
      await Promise.all([refetchVisits(), refetchClientServices(), refetchClass(), refetchWaitlist()]);
    } catch (error) {
      trackBookClassFailed(mindbodyClass, error instanceof ApiClientException ? error.ErrorCode : undefined, {
        runClub: true,
      });
      Sentry.withScope((scope) => {
        scope.setTag('where', 'ClassScreen:bookRunClub');
        scope.setExtra('classId', id);
        if (error instanceof ApiClientException) {
          scope.setTag('mb_error_code', String(error.ErrorCode));
          scope.setExtra('mb_message', error.Message);
        }
        Sentry.captureException(error);
      });

      const bookingFallback = resolveUserError('booking');
      let title = bookingFallback.title;
      let description: string | undefined = bookingFallback.description;

      if (error instanceof ApiClientException) {
        if (error.Message?.includes('ClassCapacityReached')) {
          title = 'Ce Run Club est complet';
          description = 'Toutes les places ont été réservées. Consultez les prochaines séances Run Club.';
        } else if (error.Message?.includes('AlreadyBooked') || error.Message?.includes('already')) {
          title = 'Vous êtes déjà inscrit à ce Run Club';
          description = undefined;
        } else {
          title = error.getMessage();
          description = resolveUserError('booking', error).description;
        }
      }

      toast({ title, description, status: 'error', isClosable: true });
    } finally {
      setIsLoadingServiceConfirmation(false);
    }
  };

  const onSubmit = async () => {
    // Run Club: flux dédié sans modale de sélection de crédits
    if (isRunClub) {
      await bookRunClub();
      return;
    }

    try {
      const { data: refreshedServices } = await refetchClientServices();
      const data = refreshedServices
        ? filterAllowedServices(Object.values(refreshedServices), allowedServiceNames)
        : refreshedServices;
      if (!data) {
        // Capture the exception with Sentry
        const error = new Error('Data is undefined');
        Sentry.withScope((scope) => {
          scope.setTag('where', 'ClassScreen:onSubmit');
          scope.setExtra('context', 'Fetching client services');
          scope.setExtra('data', data);
          Sentry.captureException(error);
        });
        throw error;
      }
      const [service] = data;

      if (isCommunityClass) {
        // Pour les community classes, passer le SpotNumber si un spot a été sélectionné
        const body: Partial<ReservationFormValues> = {
          isCommunityClass: true,
        };
        if (selectedSpot) {
          body.SpotNumber = selectedSpot;
        }
        await bookClass(body);
        return;
      }

      if (classIsFull) {
        if (waitlistEntry) {
          await HTTP.delete(`/class/${id}/waitlist`).send();
          return queryClient.setQueryData(['/class', id, '/waitlist'], null);
        }

        const isWaitlist = true;
        if (data?.length === 1) {
          // Premier breadcrumb pour service unique
          sentryTrace('Single service available, confirming selection', {
            level: 'info',
            tags: {
              category: 'service-selection',
            },
            extra: {
              isWaitlist: isWaitlist,
              serviceId: service.Id,
              user: `${user?.FirstName} ${user?.LastName}`,
              email: user?.Email,
            },
          });
          confirmServiceSelection(service.Id, isWaitlist);
        } else {
          // Deuxième breadcrumb pour services multiples
          sentryTrace('Multiple services available, opening selection modal', {
            level: 'info',
            tags: {
              category: 'service-selection',
            },
            extra: {
              isWaitlist: isWaitlist,
              user: `${user?.FirstName} ${user?.LastName}`,
              email: user?.Email,
            },
          });
          onOpen();
        }
      }

      if (isEnrollmentInURL || hasNoRoomLayout || isCourse) {
        return onOpen();
      }

      if (!selectedSpot) return;

      if (!isCommunityClass && data?.length === 1) {
        confirmServiceSelection(service.Id); // If only one service
      } else {
        onOpen();
      }
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag('where', 'ClassScreen:onSubmit');
        scope.setExtra('selectedSpot', selectedSpot);
        scope.setExtra('isEnrollmentInURL', isEnrollmentInURL);
        if (e instanceof ApiClientException) {
          scope.setTag('mb_error_code', String(e.ErrorCode));
          scope.setExtra('mb_message', e.Message);
        }
        Sentry.captureException(e);
      });

      return null;
    }
  };

  const confirmServiceSelection = async (serviceId?: number, isWaitlist?: boolean) => {
    if (classIsFull && isWaitlist === undefined) isWaitlist = true;

    // Troisième breadcrumb pour tentative de sélection de service
    sentryTrace('Attempting to confirm service selection', {
      level: 'info',
      tags: {
        category: 'service-selection',
      },
      extra: {
        classId: id,
        selectedService: selectedService,
        serviceId: serviceId,
        isEnrollmentInURL: isEnrollmentInURL,
        isWaitlist: isWaitlist,
        selectedSpot: selectedSpot,
        user: `${user?.FirstName} ${user?.LastName}`,
        email: user?.Email,
      },
    });

    if (
      !id ||
      (!selectedService && !serviceId) ||
      (!isEnrollmentInURL && !hasNoRoomLayout && !isCourse && !isWaitlist && !selectedSpot)
    ) {
      // Quatrième breadcrumb pour paramètres manquants
      Sentry.captureMessage('Exiting confirmServiceSelection due to missing parameters', {
        level: 'warning',
        tags: {
          category: 'service-selection',
        },
        extra: {
          classId: id,
          selectedService: selectedService,
          serviceId: serviceId,
          isEnrollmentInURL: isEnrollmentInURL,
          isWaitlist: isWaitlist,
          selectedSpot: selectedSpot,
          user: `${user?.FirstName} ${user?.LastName}`,
          email: user?.Email,
        },
      });
      return;
    }

    setIsLoadingServiceConfirmation(true);

    const body: ReservationFormValues = {
      SpotNumber: isEnrollmentInURL || isWaitlist || hasNoRoomLayout || isCourse ? undefined : selectedSpot, // Allow undefined SpotNumber if it's an enrollment, waitlist, course, or has no room layout
      classId: id,
      serviceId: selectedService ?? serviceId,
      Waitlist: isWaitlist ? true : undefined,
    };

    if (waitlistEntry) body.Waitlist = waitlistEntry.Id;

    // Book class
    await bookClass(body);
    setIsLoadingServiceConfirmation(false);
    onClose();
  };

  const isBooked =
    isEnrollmentInURL || hasNoRoomLayout || isCourse
      ? visits?.some(
          (visit) =>
            visit.ClassId === mindbodyClass?.Id && visit.AppointmentStatus === 'Booked' && visit.ClientId === user?.Id,
        )
      : !!mindbodyClass?.PickASpot?.reservations.find((reservation) => reservation.memberExternalId === user?.Id);

  /* Not logged in */
  if (!isLoggedIn)
    return (
      <Stack>
        <Heading>{t('Connectez vous pour réserver')}</Heading>
        <Auth />
      </Stack>
    );

  /* Loading */
  const isLoading = classLoading;
  if (isLoading) return <ClassLoading />;

  /* Required service not found*/
  if (error && error.ErrorCode === 'ServicePurchaseRequired') {
    const useNewPricing = typeof mondayMindbody !== 'undefined' && mondayMindbody?.pricingConfig?.enabled === true;
    return (
      <Stack>
        <Heading>{t('Recharger votre compte')}</Heading>
        {/* Framework Wix UX : ce qui s'est passé, pourquoi, rassurance, comment résoudre. */}
        <Text textAlign="center" color="gray.600" maxW="520px" alignSelf="center">
          {t(
            'Aucun de vos crédits ne permet de réserver cette séance : pack épuisé, expiré, pas encore actif ou valable dans un autre studio. Votre place n’a pas été réservée et rien n’a été débité. Choisissez une formule ci-dessous pour continuer.'
          )}
        </Text>
        {useNewPricing ? <ServicesContracts /> : <Services classId={id} />}
      </Stack>
    );
  }

  if (!mindbodyClass || !id) return null;
  const classIsFull = !classHasCapacity(mindbodyClass);

  const isCommunityClass = /communauté|communaute/i.test(mindbodyClass?.ClassDescription?.Name ?? '');

  const isWaitlistFull = (mindbodyClass: MindbodyClass) => {
    return mindbodyClass.TotalBookedWaitlist >= mindbodyClass.WaitListSize;
  };

  function getSubmitButtonLabel() {
    if (waitlistEntry && classIsFull) return t("Quitter la file d'attente");
    if (waitlistEntry) return t('Confirmer ma réservation');
    if (isBooked) return t('Réserver un autre spot');
    if (classIsFull) return t("Ajoutez-moi à la liste d'attente");
    return t('Valider ma réservation');
  }
  /* Reservation form */
  return (
    <Stack>
      <Center>
        <Button mt={4} variant="transparent" className="backButton" type="submit" onClick={() => navigate('/')}>
          {t('Retour')}
        </Button>
      </Center>
      <Stack alignItems="center">
        <Text className="classDate" fontSize="xl" fontWeight="semibold" textTransform="capitalize" color="black">
          {format(new Date(mindbodyClass.StartDateTime), "iiii d MMMM ' - ' HH:mm", { locale: fr })}
        </Text>
        <Text fontSize="xl" fontWeight="semibold" color={primaryColor} className="classDescriptionName">
          {mindbodyClass.ClassDescription.Name.toLowerCase()} avec{' '}
          {capitalizeFirstLetter(mindbodyClass.Staff.FirstName)}
        </Text>
        {mindbodyClass.ThemeName && (
          <Text fontSize="lg" fontWeight="medium" color="black" className="classThemeName">
            {mindbodyClass.ThemeName}
          </Text>
        )}
        <Text
          fontSize="normal"
          textTransform="uppercase"
          fontWeight="medium"
          color="black"
          className="classDescriptionName"
          mb={2}
        >
          studio {mindbodyClass?.Location.Name}
        </Text>
      </Stack>

      {isMultiCreditClass && !isBooked && (
        <Center>
          <Flex
            className="multiCreditBanner"
            alignItems="center"
            gap={4}
            maxW="520px"
            w="full"
            mx={{ base: 4, md: 0 }}
            mt={2}
            mb={1}
            px={5}
            py={4}
            bg="white"
            borderWidth="1px"
            borderColor="blackAlpha.200"
            borderLeftWidth="4px"
            borderLeftColor={primaryColor}
            borderRadius="md"
            boxShadow="sm"
          >
            <Center
              minW="44px"
              h="44px"
              borderRadius="full"
              bg={`${primaryColor}1A`}
              color={primaryColor}
              fontWeight="bold"
              fontSize="lg"
              flexShrink={0}
            >
              {creditsRequired}×
            </Center>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="black" lineHeight="1.3">
                Session spéciale — {creditsRequired} crédits par réservation
              </Text>
              <Text fontSize="xs" color="gray.600" mt={1} lineHeight="1.4">
                Cet événement consomme {creditsRequired} crédits sur votre forfait. Pensez à vérifier
                votre solde avant de réserver.
              </Text>
            </Box>
          </Flex>
        </Center>
      )}

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ClassRoomLayout classId={id} isEnrollmentInURL={isEnrollmentInURL} />
          {mondayMindbody.siteId === '5736526' && (
            <Text
              className="classNotice"
              color="gray.400"
              fontSize="normal"
              fontWeight="normal"
              textAlign="center"
              mt={5}
              mb={1}
              display={isEnrollmentInURL === true ? 'none' : 'block'}
            >
              Le premier rang est recommandé aux riders réguliers. C'est vous qui fixerez le rythme pendant la session.
            </Text>
          )}
          <ButtonGroup w="full" justifyContent="center" flexDirection={{ base: 'column', md: 'row' }}>
            {(!isCommunityClass || (isCommunityClass && !isBooked)) && !(isBooked && isEnrollmentInURL) && (
              <Button
                isDisabled={
                  (!selectedSpot && !classIsFull && !isEnrollmentInURL && !hasNoRoomLayout && !isCourse) ||
                  (classIsFull && isWaitlistFull(mindbodyClass) && !waitlistEntry)
                }
                mt={4}
                variant="contained"
                className="bookButton"
                type="submit"
                isLoading={formState.isSubmitting}
              >
                {isLoadingServiceConfirmation ? <Spinner color="white" size="sm" /> : getSubmitButtonLabel()}
              </Button>
            )}
            {isBooked && (
              <Button
                mt={4}
                className="seeAllBookings"
                variant="transparent"
                type="submit"
                onClick={() => navigate('/account/visits')}
              >
                {t('Voir toutes mes réservations')}
              </Button>
            )}
          </ButtonGroup>
          {showSuccessMessage && (
            <Stack mt={4} spacing={1} alignItems="center">
              <Text
                className="bookingSuccessMessage"
                color="green.500"
                fontSize="lg"
                fontWeight="bold"
                textAlign="center"
              >
                Votre réservation a bien été prise en compte
              </Text>
              {isMultiCreditClass && (
                <Text className="bookingSuccessCreditsHint" color="gray.600" fontSize="sm" textAlign="center">
                  {creditsRequired} crédits ont été décomptés de votre forfait.
                </Text>
              )}
            </Stack>
          )}
        </form>
      </FormProvider>
      {/* Service Selection Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent className="modalServicesChoice">
          <ModalHeader>Crédits à utiliser</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isMultiCreditClass && (
              <Text fontSize="sm" mb={3} className="multiCreditNotice">
                Ce cours nécessite <strong>{creditsRequired} crédits</strong> sur votre forfait.
              </Text>
            )}
            {allowedServiceNames && clientServices.length === 0 && (
              <Text fontSize="sm" mb={3} className="noAllowedServiceNotice">
                Aucun de vos crédits ne peut être utilisé pour cette séance : elle est réservée à des tarifs précis.
              </Text>
            )}
            <Select placeholder="Choisir des crédits" onChange={(e) => setSelectedService(parseInt(e.target.value))}>
              {(hasAlreadyUsedRoutineService ? noRoutineServices : clientServices)?.map((service) => {
                const remaining = service.Remaining ?? 0;
                const isInsufficient = remaining < creditsRequired;
                const suffix = isMultiCreditClass ? ` (${remaining} restants)` : '';
                return (
                  <option key={service.Id} value={service.Id} disabled={isInsufficient}>
                    {service.Name}
                    {suffix}
                    {isInsufficient ? ' — crédits insuffisants' : ''}
                  </option>
                );
              })}
            </Select>
            {selectedServiceData && !hasEnoughCreditsOnSelectedService && (
              <Text fontSize="sm" mt={3} color="red.500">
                Ce forfait ne contient que {selectedServiceData.Remaining ?? 0} crédit
                {(selectedServiceData.Remaining ?? 0) > 1 ? 's' : ''}, il en faut {creditsRequired}.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="ghost" onClick={onClose} textTransform={'uppercase'}>
                Fermer
              </Button>
              <Button
                borderRadius={buttonBorderRadius}
                textTransform={'uppercase'}
                minWidth="120px"
                colorScheme="primary"
                onClick={() => confirmServiceSelection()}
                isDisabled={!selectedService || !hasEnoughCreditsOnSelectedService}
              >
                {isLoadingServiceConfirmation ? <Spinner color="white" size="sm" /> : t('Confirmer')}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
}

function ClassLoading() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Stack>
      <Center>
        <Button mt={4} variant="transparent" className="backButton" type="submit" onClick={() => navigate('/')}>
          {t('Retour')}
        </Button>
      </Center>
      <Stack alignItems="center">
        <Skeleton w={150} h="40px" />
        <Skeleton w={120} h="30px" />
        <Skeleton w={100} h="45px" />
      </Stack>

      <Center>
        <ClassRoomLayoutLoadingPlaceholder />
      </Center>
      <Center w="full">
        <Skeleton w={200} h="40px" />
      </Center>
    </Stack>
  );
}
